import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Rocket, User, Scale, Stethoscope, Building2, Plane, ShieldCheck, Check, Loader2,
  MessageSquareWarning, Lightbulb, X, PlusCircle, Sparkles, ChevronRight, Clock,
} from "lucide-react";
import { EnterpriseCrmSection, EnterpriseValuePanel } from "@/components/EnterpriseCrm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = [
  { key: "client", icon: User, title: "Client", time: "3–4 min",
    what: "An accident victim documents the crash, verifies identity, and signs authorizations.",
    why: "Clients are the start of every case — Crash Ready turns chaos into a guided, verified intake.",
    notifies: "Retainer, telehealth authorization, appointment confirmations, reminders.",
    talks: "RN, Attorney, Telehealth Provider" },
  { key: "rn", icon: Stethoscope, title: "Registered Nurse", time: "2–3 min",
    what: "Reviews intakes, verifies identity/insurance/storyline, approves or escalates cases.",
    why: "RN vetting means attorneys only receive medically-screened, verified cases.",
    notifies: "New intake offers, escalations, payout events.",
    talks: "Client, Attorney" },
  { key: "attorney", icon: Scale, title: "Attorney", time: "3–4 min",
    what: "Auto-accepts RN-verified cases, sends retainers, receives charts, runs the Command Center.",
    why: "Higher-quality signed cases with medical documentation — inside your existing CRM workflow.",
    notifies: "New case matches, signed retainers, released charts, pipeline updates.",
    talks: "Client, RN, Telehealth Provider, Drone Pilot" },
  { key: "telehealth", icon: Building2, title: "Telehealth Provider", time: "2–3 min",
    what: "Accepts referrals, schedules visits, sends forms, charts, and releases records to attorneys.",
    why: "A coordinated treatment network with automatic referrals and record delivery.",
    notifies: "New referrals, bookings, form completions, chart acknowledgments.",
    talks: "Client, Attorney" },
  { key: "pilot", icon: Plane, title: "Drone Pilot", time: "1–2 min",
    what: "FAA Part 107 pilots receive dispatches, document accident scenes, and deliver evidence packages.",
    why: "Time-critical scene evidence, captured professionally, straight into the case file.",
    notifies: "Dispatch offers, mission updates, payout events.",
    talks: "Attorney, Administrator" },
  { key: "admin", icon: ShieldCheck, title: "Administrator", time: "3–5 min", code: true,
    what: "Runs the Network Operations Center: approvals, payouts, analytics, live activity, test controls.",
    why: "Complete operational visibility over every professional network, nationwide.",
    notifies: "Applications, change requests, escalations, payments, everything.",
    talks: "Every role" },
];

const ORDER = ["Client", "RN", "Attorney", "Telehealth", "Drone Pilot", "Administrator"];

const SCENARIOS = [
  ["Perfect Rear-End Accident", "Everything fires automatically — Tom Hanks' case.", "client"],
  ["Insurance Delay", "Reminders keep a slow case moving — Amanda Hanks.", "client_amanda"],
  ["RN Requests Additional Information", "RN review loop before approval.", "rn"],
  ["Attorney Declines → Reroute", "Routing engine finds the next qualified firm.", "attorney"],
  ["Telehealth Provider Declines → Reroute", "Brenda Carpenter's recovery path.", "client_brenda"],
  ["Drone Deployment", "Pilot accepts, documents the scene, returns evidence.", "pilot"],
  ["Medical Records Returned", "Charts land in the Attorney Document Center.", "attorney"],
  ["Settlement Pipeline", "Demand-prep progression in the Firm Command Center.", "attorney"],
];

const CRM_POINTS = ["Auto-created case notes", "Auto-uploaded documents", "Auto-generated timelines",
  "Auto-generated treatment summaries", "CRM synchronization", "One-click “Open in My CRM”", "Live sync indicators"];

const SMS_NUMBER = "3365825112";

function smsHref(context) {
  const body = `Crash Ready Pre-Beta Feedback%0ARole: ${context.role || ""}%0ACurrent Screen: ${context.page || ""}%0AIssue / Suggestion:%0ASteps to Reproduce:%0AExpected Result:%0AActual Result:%0AAdditional Comments:%0A---%0APage: ${encodeURIComponent(window.location.href)}%0ABrowser: ${encodeURIComponent(navigator.userAgent.slice(0, 60))}%0AApp: pre-beta · ${new Date().toISOString()}%0ASession: ${context.session_id || "n/a"}`;
  return `sms:${SMS_NUMBER}?&body=${body}`;
}

export default function TestDrive() {
  const [started, setStarted] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [adminCode, setAdminCode] = useState("");
  const [askCode, setAskCode] = useState(false);
  const [feedback, setFeedback] = useState(null); // "bug" | "feature"

  const load = () => axios.get(`${API}/test-drive/overview`).then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const launch = async (role, code) => {
    setBusy(role);
    try {
      const { data } = await axios.post(`${API}/test-drive/launch`, { role, code });
      localStorage.setItem("cr_token", data.token);
      sessionStorage.setItem("td_session", data.session_id);
      sessionStorage.setItem("td_role", data.role_label);
      toast.success(`Launching ${data.role_label} demo…`);
      window.location.href = data.redirect;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not launch demo"); }
    finally { setBusy(""); }
  };

  const liveClient = async () => {
    setBusy("live");
    try {
      const { data } = await axios.post(`${API}/test-drive/live-client`);
      localStorage.setItem("cr_token", data.token);
      sessionStorage.setItem("td_role", "Live Test Client");
      toast.success(data.note, { duration: 8000 });
      window.location.href = data.redirect;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create live client"); }
    finally { setBusy(""); }
  };

  if (!started) {
    return (
      <div className="min-h-screen bg-navy text-white flex items-center justify-center px-6" data-testid="td-mission">
        <div className="max-w-2xl text-left space-y-6 py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold tracking-widest uppercase"><Sparkles className="w-3.5 h-3.5" /> Pre-Beta Stress Test</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold leading-tight">Welcome to the Crash Ready<br />Pre-Beta Stress Test</h1>
          <p className="text-lg font-semibold text-white/90">Building the Future of Accident Response Together</p>
          <div className="space-y-4 text-white/70 text-sm leading-relaxed">
            <p>Thank you for accepting our invitation. You are among the very first professionals to experience Crash Ready before our national launch.</p>
            <p>Crash Ready connects clients, attorneys, registered nurses, telehealth providers, drone pilots, investigators, and administrators into one coordinated accident response ecosystem.</p>
            <p className="text-white font-semibold">Crash Ready doesn't replace your workflow.<br />Crash Ready Makes Your CRM Smarter™</p>
            <p>Our mission is simple: help accident victims receive faster care, help attorneys receive higher-quality cases, help medical providers join a coordinated treatment network — and build the most intelligent accident response platform ever created.</p>
            <p>Every piece of feedback you provide directly shapes the future of this platform. <span className="text-white font-semibold">Welcome to the founding team.</span></p>
          </div>
          <button onClick={() => setStarted(true)} data-testid="td-begin"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-cobalt text-white font-heading font-bold text-lg hover:-translate-y-0.5 transition-all shadow-lg shadow-cobalt/30">
            <Rocket className="w-5 h-5" /> Begin Test Drive
          </button>
          {data?.expires_at && <p className="text-xs text-white/40">Test access expires {new Date(data.expires_at).toLocaleDateString()} · TEST MODE — no real funds, no real medical or legal services.</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="td-center">
      <header className="bg-navy text-white px-6 py-10">
        <div className="max-w-6xl mx-auto">
          <h1 className="font-heading text-3xl font-extrabold">Crash Ready Test Drive Center</h1>
          <p className="text-white/70 text-sm mt-2 max-w-2xl">Pick a role and launch a live demo — every account is preloaded with completed work, pending tasks, notifications, and payouts. No waiting on other testers.</p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <span className="px-3 py-1 rounded-full bg-white/10">Recommended order:</span>
            {ORDER.map((o, i) => <span key={o} className="px-3 py-1 rounded-full bg-white/10 flex items-center gap-1">{i + 1}. {o}{i < ORDER.length - 1 && <ChevronRight className="w-3 h-3 opacity-50" />}</span>)}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 space-y-12 mt-10">
        <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {ROLES.map((r) => (
            <div key={r.key} className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col" data-testid={`td-role-${r.key}`}>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-2xl bg-cobalt/10 flex items-center justify-center"><r.icon className="w-5 h-5 text-cobalt" /></div>
                <div>
                  <h3 className="font-heading font-bold text-navy">{r.title}</h3>
                  <div className="text-[11px] text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {r.time} demo</div>
                </div>
              </div>
              <div className="mt-3 space-y-2 text-xs text-slate-500 flex-1">
                <p><b className="text-navy">What:</b> {r.what}</p>
                <p><b className="text-navy">Why:</b> {r.why}</p>
                <p><b className="text-navy">Notifications:</b> {r.notifies}</p>
                <p><b className="text-navy">Works with:</b> {r.talks}</p>
              </div>
              {r.code && askCode ? (
                <div className="mt-4 flex gap-2">
                  <input value={adminCode} onChange={(e) => setAdminCode(e.target.value)} placeholder="Access code" data-testid="td-admin-code"
                    className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm" />
                  <button onClick={() => launch("admin", adminCode)} data-testid="td-admin-go" className="px-4 py-2 rounded-full bg-navy text-white text-xs font-semibold">Go</button>
                </div>
              ) : (
                <button onClick={() => (r.code ? setAskCode(true) : launch(r.key))} disabled={busy === r.key} data-testid={`td-launch-${r.key}`}
                  className="mt-4 w-full py-3 rounded-full bg-navy text-white font-semibold text-sm hover:bg-cobalt transition-colors disabled:opacity-50">
                  {busy === r.key ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Launch Demo"}
                </button>
              )}
            </div>
          ))}
        </section>

        <section data-testid="td-enterprise-crm" className="space-y-4">
          <EnterpriseCrmSection compact />
          <EnterpriseValuePanel />
        </section>

        <section className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="td-live-engine">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-heading text-xl font-bold text-navy">Live Scenario Engine</h2>
              <p className="text-sm text-slate-500 mt-1 max-w-xl">Become a brand-new accident victim and run the <b>real</b> intake. Your case travels Client → RN → Attorney → Telehealth → Attorney using the actual routing engine — no mocked statuses, no fake buttons.</p>
            </div>
            <button onClick={liveClient} disabled={busy === "live"} data-testid="td-live-client"
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-cobalt text-white font-semibold text-sm hover:-translate-y-0.5 transition-all">
              {busy === "live" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />} Create New Live Test Client
            </button>
          </div>
        </section>

        <section data-testid="td-trackers">
          <h2 className="font-heading text-xl font-bold text-navy mb-4">Live Workflow Tracker</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {(data?.trackers || []).map((t) => (
              <div key={t.name} className="bg-white rounded-3xl border border-slate-100 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`td-tracker-${(t.name || "").split(" ")[0]?.toLowerCase()}`}>
                <div className="flex items-center justify-between">
                  <div><div className="font-heading font-bold text-navy">{t.name}</div><div className="text-[11px] text-slate-400">{t.scenario} · {t.case_number || ""}</div></div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${t.completed ? "bg-emerald-50 text-emerald-700" : "bg-cobalt/10 text-cobalt"}`}>{t.completed ? "COMPLETED" : `${t.pct}%`}</span>
                </div>
                <div className="mt-3 space-y-1.5">
                  {t.milestones.map((m) => (
                    <div key={m.label} className="flex items-center gap-2 text-xs">
                      <span className={`h-4 w-4 rounded-full flex items-center justify-center ${m.done ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-300"}`}><Check className="w-3 h-3" /></span>
                      <span className={m.done ? "text-navy" : "text-slate-400"}>{m.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="td-feed">
            <h2 className="font-heading text-lg font-bold text-navy">Real-Time Activity Feed</h2>
            <div className="mt-3 space-y-2 max-h-72 overflow-y-auto">
              {(data?.activity_feed || []).map((e, i) => (
                <div key={i} className="flex items-start gap-2 text-xs border-b border-slate-50 pb-2">
                  <span className="mt-0.5 h-2 w-2 rounded-full bg-cobalt shrink-0" />
                  <div><span className="text-navy font-medium">{e.title}</span>
                    <div className="text-slate-400">{e.actor_name || "System"} · {e.occurred_at?.slice(0, 19).replace("T", " ")}</div></div>
                </div>
              ))}
              {(!data?.activity_feed || data.activity_feed.length === 0) && <p className="text-xs text-slate-400 py-4">Activity will appear here as demos run.</p>}
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="td-analytics">
            <h2 className="font-heading text-lg font-bold text-navy">Demo Analytics</h2>
            <div className="mt-4 grid grid-cols-2 gap-4">
              {[["Total Testers", data?.analytics?.total_testers], ["Completed Demonstrations", data?.analytics?.completed_demonstrations],
                ["Feedback Received", data?.analytics?.feedback_received], ["Scenario Completion", `${data?.analytics?.completion_pct ?? 0}%`]].map(([l, v]) => (
                <div key={l} className="bg-slate-50 rounded-2xl p-4">
                  <div className="font-heading text-2xl font-extrabold text-navy">{v ?? 0}</div>
                  <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="td-scenarios">
          <h2 className="font-heading text-xl font-bold text-navy mb-4">Demo Scenarios</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SCENARIOS.map(([title, desc, role], i) => (
              <button key={title} onClick={() => launch(role)} data-testid={`td-scenario-${i + 1}`}
                className="text-left bg-white rounded-2xl border border-slate-100 p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-cobalt hover:-translate-y-0.5 transition-all">
                <div className="text-[10px] font-bold text-cobalt uppercase tracking-wider">Scenario {i + 1}</div>
                <div className="font-heading font-semibold text-navy text-sm mt-1">{title}</div>
                <div className="text-xs text-slate-400 mt-1">{desc}</div>
              </button>
            ))}
          </div>
        </section>

        <section className="bg-navy rounded-3xl p-8 text-white" data-testid="td-crm">
          <h2 className="font-heading text-2xl font-extrabold">Crash Ready Makes Your CRM Smarter™</h2>
          <p className="text-white/70 text-sm mt-2 max-w-2xl">We enhance your existing workflow — we don't ask you to replace it. Crash Ready is an intelligence layer that works alongside Clio, Filevine, Litify, CASEpeer, Needles, MyCase, PracticePanther, Smokeball, and more.</p>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {CRM_POINTS.map((p) => (
              <div key={p} className="flex items-center gap-2 text-xs bg-white/10 rounded-xl px-3 py-2.5"><Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> {p}</div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-3xl border border-slate-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="td-help-build">
          <h2 className="font-heading text-2xl font-extrabold text-navy">Help Build Crash Ready</h2>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">You are participating in Crash Ready's official Pre-Beta Stress Test. Every bug you report helps improve the platform before launch.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button onClick={() => setFeedback("bug")} data-testid="td-report-bug"
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-red-600 text-white font-semibold text-sm hover:bg-red-700"><MessageSquareWarning className="w-4 h-4" /> Report Bug</button>
            <button onClick={() => setFeedback("feature")} data-testid="td-suggest-feature"
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-cobalt text-white font-semibold text-sm hover:bg-cobalt/90"><Lightbulb className="w-4 h-4" /> Suggest Feature</button>
            <a href={smsHref({ role: sessionStorage.getItem("td_role"), page: "Test Drive Center", session_id: sessionStorage.getItem("td_session") })}
              data-testid="td-sms-founder" className="flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-slate-200 text-navy font-semibold text-sm hover:bg-slate-50">
              💬 Text the Founder directly
            </a>
          </div>
          <div className="mt-6 rounded-2xl bg-slate-50 p-4 text-xs text-slate-500" data-testid="td-commitment">
            <b className="text-navy">Response Commitment:</b> For any reproducible bug submitted during Pre-Beta testing, our goal is to investigate immediately and deploy a fix within two hours whenever reasonably possible. More complex issues may require additional time, but every report is reviewed and prioritized.
          </div>
        </section>

        <section className="text-center py-6" data-testid="td-founding">
          <h2 className="font-heading text-2xl font-extrabold text-navy">Thank You</h2>
          <p className="text-sm text-slate-500 mt-2 max-w-xl mx-auto">Every person participating in this Pre-Beta Stress Test becomes one of Crash Ready's <b className="text-navy">Founding Testers</b>. The feedback you provide today directly shapes the national launch. Thank you for helping us build the future of accident response.</p>
          {data?.expires_at && <p className="text-[11px] text-slate-400 mt-3">Test access expires {new Date(data.expires_at).toLocaleDateString()} · TEST MODE — NO REAL FUNDS MOVED</p>}
        </section>
      </main>

      <a href={smsHref({ role: sessionStorage.getItem("td_role"), page: window.location.pathname, session_id: sessionStorage.getItem("td_session") })}
        data-testid="td-floating-feedback"
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 px-5 py-3 rounded-full bg-navy text-white text-sm font-semibold shadow-2xl hover:bg-cobalt transition-colors">
        💬 Report Bug or Suggest Feature
      </a>

      {feedback && <FeedbackModal kind={feedback} onClose={() => setFeedback(null)} />}
    </div>
  );
}

function FeedbackModal({ kind, onClose }) {
  const [f, setF] = useState({ description: "", expected: "", actual: "", steps: "", why: "", frequency: "", severity: "medium", role: sessionStorage.getItem("td_role") || "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.description.trim()) { toast.error(kind === "bug" ? "Describe the bug" : "Describe your idea"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/test-drive/feedback`, {
        kind, ...f, page: window.location.href, device: navigator.platform,
        browser: navigator.userAgent.slice(0, 120), session_id: sessionStorage.getItem("td_session"),
      });
      toast.success("Thank you, Founding Tester! Report received.");
      onClose();
    } catch { toast.error("Could not submit — try the SMS button instead"); }
    finally { setBusy(false); }
  };
  const T = (k, label, ph, rows = 2) => (
    <div><label className="text-[11px] font-semibold text-slate-500 uppercase">{label}</label>
      <textarea value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} rows={rows} placeholder={ph}
        data-testid={`td-fb-${k}`} className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" /></div>
  );
  return (
    <div className="fixed inset-0 z-[70] bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="td-feedback-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-navy">{kind === "bug" ? "Report a Bug" : "Suggest a Feature"}</h3>
          <button onClick={onClose} data-testid="td-fb-close" className="p-1.5 rounded-full hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-slate-400 mb-3">Page, role, device, browser, time, and session ID are attached automatically.</p>
        {T("description", kind === "bug" ? "Bug description" : "Your idea", kind === "bug" ? "What went wrong?" : "What should we build?", 3)}
        {kind === "bug" ? (<>
          {T("expected", "Expected result", "What should have happened?")}
          {T("actual", "Actual result", "What actually happened?")}
          {T("steps", "Reproduction steps", "1. …  2. …")}
          <div className="mt-2"><label className="text-[11px] font-semibold text-slate-500 uppercase">Severity</label>
            <select value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })} data-testid="td-fb-severity"
              className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
              {["low", "medium", "high", "critical"].map((s) => <option key={s}>{s}</option>)}
            </select></div>
        </>) : (<>
          {T("why", "Why it would improve the platform", "The impact…")}
          {T("frequency", "How often you'd use it", "Daily / weekly / per case…")}
        </>)}
        <button onClick={submit} disabled={busy} data-testid="td-fb-submit"
          className="mt-4 w-full py-3 rounded-full bg-navy text-white font-semibold text-sm disabled:opacity-50">
          {busy ? "Sending…" : "Send to the build team"}
        </button>
      </div>
    </div>
  );
}
